import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, RotateCcw, Trash2, ImageOff } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'

interface AppDesignLogoProps {
  isEditMode: boolean
  isSaving: boolean
  logoUrl: string | null | undefined
  onLogoSelected: (file: File, dataUrl: string) => void
  onResetClick: () => void
  onDeleteClick: () => void
}

export function AppDesignLogo({
  isEditMode,
  isSaving,
  logoUrl,
  onLogoSelected,
  onResetClick,
  onDeleteClick,
}: AppDesignLogoProps) {
  const { t } = useTranslation(['configuration', 'common'])
  const inputRef = useRef<HTMLInputElement>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (evt) => {
      const dataUrl = evt.target?.result as string
      onLogoSelected(file, dataUrl)
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold">{t('logo.title')}</p>

      <div className="w-[150px] h-[150px] flex items-center justify-center rounded-lg border bg-muted/40 p-2 overflow-hidden">
        {logoUrl ? (
          <img src={logoUrl} className="max-w-full max-h-full object-contain" alt={t('logo.alt')} />
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <ImageOff className="size-8" />
            <span className="text-xs text-center">{t('logo.noLogo')}</span>
          </div>
        )}
      </div>

      {isEditMode && !isSaving && (
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/gif, image/jpeg, image/png"
            hidden
            onChange={handleInputChange}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={() => inputRef.current?.click()}>
                <Upload className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('logo.upload')}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={onResetClick}>
                <RotateCcw className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('buttons.reset', { ns: 'common' })}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={onDeleteClick} className="text-destructive hover:text-destructive">
                <Trash2 className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('buttons.delete', { ns: 'common' })}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  )
}
